import React, {
    MutableRefObject,
    Suspense,
    ReactElement,
    useState,
    useEffect,
    useRef
} from "react";

import { css, keyframes } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Spinner from "@atlaskit/spinner";
import { token } from "@atlaskit/tokens";

import { GlobalNavUserProfile } from "@/components/global-nav/user-profile-new/src";
import { useProductIntent } from "@/components/sites-provider/helpers";
import { TryIntent } from "@/components/try-intent/src/try-intent.tsx";
import { Button, Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { GrowthUnifiedProfileClient } from "@/libraries/growth-unified-profile-client/client";
import { OptionalFields } from "@/libraries/growth-unified-profile-client/types";
import {
    GUPSProductKeys,
    GrowthUnifiedProfile
} from "@/libraries/growth-unified-profile-client/unified-profile.schema";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Icon } from "@/libraries/wpl/foundations/src";
import { LogoV3 } from "@/libraries/wpl/foundations/src";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";
import { Cookie } from "@/utils/cookie";
import { experiments, useExperiment, ExperimentView } from "@/utils/experiment";
import { useFeatureExposed } from "@/utils/feature-exposed";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { getLocalStorage } from "@/utils/localstorage";

import { useLoggedInUser } from "../../../utils/logged-in-user-provider";

import { ContactSalesCta } from "./components/contact-sales-cta";
import NotificationsComponent from "./global-nav-notifications/components/notifications";
import SearchButton from "./search-button";

export type SearchAndProfileProps = {
    isShowTryNowLink?: boolean;
    productKey?: ProductKeyTypes;
    isShowSearchButton?: boolean;
    setIsOpenSearch: React.Dispatch<React.SetStateAction<boolean>>;
    searchAndProfileRef: MutableRefObject<HTMLDivElement | null>;
    navigationTrynowCtaText?: string;
    navigationTrynowCtaUrl?: string;
    navigationTrynowCtaOnClick?: (e: React.MouseEvent) => void;
    isDisplayDivider?: boolean;
    positionTryNowCtaOnRight?: boolean;
    isShowAppSwitcher?: boolean;
    navType?: "globalNav" | "productNav";
    navigationTrynowCtaButtonVariant?: "primary" | "secondary";
};
export interface ProductData {
    productKey: GUPSProductKeys;
    productName: string;
    href: string;
    signupHref: string;
    subtitle: string;
    productMark: ReactElement;
    locale?: string;
    onClick?: () => void;
}

export type TryNowDropdownMenuProps = {
    products: ProductData[];
    buttonText: string;
    productKey: ProductKeyTypes;
};

const dropdownStyles = css({
    display: "none",
    position: "absolute",
    insetBlockStart: "100%",
    insetInlineStart: "-50%",
    padding: "24px",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "stretch",
    borderRadius: "20px",
    background: wacToken("color.neutral.light0"),
    minWidth: "250px",
    boxShadow:
        // eslint-disable-next-line local/ensure-wac-token-usage
        "0px 0px 1px 0px var(--shadow-overlay-first, rgba(9, 30, 66, 0.31)), 0px 8px 12px 0px var(--shadow-overlay-second, rgba(9, 30, 66, 0.15)), 0px 0px 0px 1px var(--shadow-overlay-third, rgba(188, 214, 240, 0.00)) inset"
});

const displayFlex = css({
    display: "flex"
});

const dropdownItemStyles = css({
    "display": "flex",
    "flexDirection": "row",
    "alignItems": "center",
    "width": "285px",
    "height": "50px",
    "marginBlockEnd": "16px",
    "&:hover": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "& .product-title": {
            color: wacToken("color.primary.blue")
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "& .product-mark": {
            backgroundColor: wacToken("color.neutral.light200")
        }
    }
});

const productTextLayout = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: "2px"
});

const productSummaryTextStyles = css({
    color: token("color.text.accent.gray"),
    fontSize: "12px !important",
    fontFamily: "Charlie Text",
    fontStyle: "normal",
    fontWeight: "400 !important",
    margin: 0
});

const productTitleTextStyles = css({
    color: wacToken("color.primary.black"),
    fontSize: "14px !important",
    fontFamily: "Charlie Display !important",
    fontStyle: "normal",
    fontWeight: "700 !important",
    lineHeight: "1.2 !important",
    margin: 0
});

const productMarkStyles = css({
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center",
    "width": "44px",
    "height": "44px",
    "padding": "4px",
    "backgroundColor": "#f8f8f8",
    "borderRadius": "4px",
    "marginInlineEnd": "12px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& svg": {
        width: "32px",
        height: "32px"
    }
});

const tryNowContainerStyles = css({
    position: "relative",
    display: "inline-block",
    paddingBlockEnd: "14px",
    paddingBlockStart: "14px"
});

const openOverlayStyles = css({
    position: "fixed",
    display: "block",
    width: "100%",
    height: "100%",
    insetBlockStart: "0",
    insetInlineStart: "0",
    insetInlineEnd: "0",
    insetBlockEnd: "0",
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "rgba(255,255,255,0.7)",
    zIndex: "-1"
});

const closedOverlayStyles = css({
    position: "fixed",
    display: "none",
    width: "100%",
    height: "100%",
    insetBlockStart: "0",
    insetInlineStart: "0",
    insetInlineEnd: "0",
    insetBlockEnd: "0",
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "rgba(255,255,255,0.7)",
    zIndex: "-1"
});

const fadeOutKeyframes = keyframes({
    from: { opacity: 1, display: "block" },
    to: {
        opacity: 0,
        display: "none"
    }
});
const fadeOutAnimation = css({
    animation: `0.25s ${fadeOutKeyframes} linear forwards`
});
const fadeInKeyframes = keyframes({
    from: {
        opacity: 0
    },
    to: {
        opacity: 1
    }
});
const fadeInAnimation = css({
    animation: `0.25s ${fadeInKeyframes} linear forwards`
});

const rotate180deg = css({ transform: "rotate(180deg)" });

const seeAllProductsStyles = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    width: "285px",
    height: "32px",
    fontSize: "14px",
    lineHeight: "20px"
});

const caretStyles = css({
    marginInlineStart: "8px",
    transition: "transform 0.3s",
    display: "inline-block"
});

const dividerStyle = css({
    borderInlineStart: `1px solid ${wacToken("color.neutral.light300")}`,
    width: "1px",
    height: "24px",
    alignSelf: "center"
});

const appSwitcherContainerStyle = css({
    marginInlineEnd: "16px"
});

const navigationActionStyle = css({
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    margin: 0,
    padding: 0
});

const navigationActionLinkStyle = css({
    alignItems: "center",
    display: "flex",
    flexDirection: "row"
});

const WacAppSwitcher = React.lazy(() =>
    // without the line ts-ignored below TSC throws this error:
    // "Relative import paths need explicit file extensions in EcmaScript imports when '--moduleResolution' is 'node16' or 'nodenext'. Did you mean '../../../components/global-nav/app-switcher/src/index.js'?"
    // the file of course ends in .tsx, when you try to add that there is a different error:
    // "An import path can only end with a '.tsx' extension when 'allowImportingTsExtensions' is enabled."

    // @ts-ignore
    import("../../../components/global-nav/app-switcher/src/index").then(
        (module) => ({
            default: module.WacAppSwitcher
        })
    )
);

const ComprehensiveProductData = (): ProductData[] => {
    const intl = useIntl();
    return [
        {
            productKey: "jira",
            productName: "Jira",
            href: useLocalizedHref("/software/jira"),
            signupHref: useLocalizedHref(
                "/try/cloud/signup?bundle=jira-software&edition=free"
            ),
            subtitle: intl.formatMessage({
                id: "globalnav.v2.try-now-jira-subtitle",
                defaultMessage: "Flexible project management"
            }),
            productMark: <LogoV3 brand="jira" type="mark" appearance="brand" />
        },
        {
            productKey: "jsm",
            productName: "Jira Service Management",
            href: useLocalizedHref("/software/jira/service-management"),
            signupHref: useLocalizedHref(
                "/try/cloud/signup?bundle=jira-service-management&edition=free"
            ),
            subtitle: intl.formatMessage({
                id: "globalnav.v2.try-now-jsm-subtitle",
                defaultMessage: "High-velocity service delivery"
            }),
            productMark: (
                <LogoV3
                    brand="jiraservicemanagement"
                    type="mark"
                    appearance="brand"
                />
            )
        },
        {
            productKey: "confluence",
            productName: "Confluence",
            href: useLocalizedHref("/software/confluence"),
            signupHref: useLocalizedHref(
                "/try/cloud/signup?bundle=confluence&edition=free"
            ),
            subtitle: intl.formatMessage({
                id: "globalnav.v2.try-now-connie-subtitle",
                defaultMessage: "Knowledge all in one place"
            }),
            productMark: (
                <LogoV3 brand="confluence" type="mark" appearance="brand" />
            )
        },
        {
            productKey: "jpd",
            productName: "Jira Product Discovery",
            href: useLocalizedHref("/software/jira/product-discovery"),
            signupHref: useLocalizedHref(
                "/try/cloud/signup?bundle=jira-product-discovery"
            ),
            subtitle: intl.formatMessage({
                id: "globalnav.v2.try-now-jpd-subtitle",
                defaultMessage: "Capture & prioritize ideas"
            }),
            productMark: (
                <LogoV3
                    brand="jiraproductdiscovery"
                    type="mark"
                    appearance="brand"
                />
            )
        },
        // Uncomment the following block when the respective products are available in GUPs
        // {
        //     productKey: "rovo",
        //     productName: "Rovo",
        //     href: useLocalizedHref("/software/rovo"),
        //     signupHref: useLocalizedHref("/software/rovo/start"),
        //     subtitle: intl.formatMessage({
        //         id: "globalnav.v2.try-now-rovo-subtitle",
        //         defaultMessage: "Transform teamwork with AI"
        //     }),
        //     productMark: <LogoV3 brand="rovo" type="mark" appearance="brand" />
        // },
        // {
        //     productKey: "loom",
        //     productName: "Loom",
        //     href: useLocalizedHref("/software/loom"),
        //     signupHref: "https://www.loom.com/signup",
        //     subtitle: intl.formatMessage({
        //         id: "globalnav.v2.try-now-loom-subtitle",
        //         defaultMessage: "Quick, async video updates"
        //     }),
        //     productMark: <LogoV3 brand="loom" type="mark" appearance="brand" />
        // },
        // {
        //     productKey: "guard",
        //     productName: "Guard",
        //     href: useLocalizedHref("/software/access"),
        //     signupHref: "https://admin.atlassian.com/atlassian-access",
        //     subtitle: intl.formatMessage({
        //         id: "globalnav.v2.try-now-guard-subtitle",
        //         defaultMessage: "Enhanced cloud security"
        //     }),
        //     productMark: <LogoV3 brand="guard" type="mark" appearance="brand" />
        // },
        {
            productKey: "compass",
            productName: "Compass",
            href: useLocalizedHref("/software/compass"),
            signupHref: useLocalizedHref("/try/cloud/signup?bundle=compass"),
            subtitle: intl.formatMessage({
                id: "globalnav.v2.try-now-compass-subtitle",
                defaultMessage: "Elevate your developer experience"
            }),
            productMark: (
                <LogoV3 brand="compass" type="mark" appearance="brand" />
            )
        }
    ];
};

let GupsDataProcessed = false;

const LoadingSpinnerStyles = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%",
    minWidth: "292px", // set width of 340px - (2 * 24px padding)
    maxHeight: "57px", // set height of 105px - (2 * 24px padding)
    padding: "24px", // Matches the padding of dropDownStyles to align inner content
    boxSizing: "border-box" // Ensures padding is included in the element's total width and height
});

const LoadingSpinner = (
    <div css={LoadingSpinnerStyles}>
        <Spinner interactionName="load" label="Loading" appearance="inherit" />
    </div>
);

const getAnonymousId = () => {
    const analyticsAnonymousId = Cookie.readCookie("ajs_anonymous_id");
    if (!analyticsAnonymousId) {
        throw new Error("Failed to get anonymousId from Cookie");
    }

    const decodedAnonymousId = decodeURIComponent(analyticsAnonymousId).replace(
        /['"]+/g,
        ""
    );
    return decodedAnonymousId;
};

export const getGupsData = async () => {
    if (!GupsDataProcessed) {
        const anonId = getAnonymousId();

        const client = new GrowthUnifiedProfileClient();
        try {
            const { data, error } = await client.getProfile(
                {
                    anonymousId: anonId
                },
                [OptionalFields.USER_FOOTPRINTS]
            );
            if (error) {
                throw new Error(
                    `Failed to execute call to get profile: ${
                        (error as Error)?.message || error
                    }`
                );
            }
            return data;
        } catch (err) {
            console.error(
                "An unexpected error occurred while getting the profile data:",
                err
            );
        }
    }
    return null;
};

const getExistingProductsList = (data: GrowthUnifiedProfile) => {
    const products = data?.userFootprints?.products;
    return products;
};

export const getFilteredProductData = (
    productData: ProductData[],
    existingProductNames: GUPSProductKeys[]
): ProductData[] => {
    const rankedProducts = [...productData];
    const filteredProductData = productData.filter(
        (product) => !existingProductNames.includes(product.productKey)
    );

    if (filteredProductData.length >= 3) {
        return filteredProductData.slice(0, 3);
    }

    while (filteredProductData.length < 3) {
        const currProduct = rankedProducts.shift();
        const currProductKey = currProduct?.productKey;

        // opposite logic to above as we are now adding duplicates to fill the 3 product slots
        if (currProductKey && existingProductNames.includes(currProductKey)) {
            filteredProductData.push(currProduct);
        }
    }
    return filteredProductData;
};

export const TryNowDropdownMenu = ({
    products,
    buttonText
}: TryNowDropdownMenuProps) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [FilteredProductData, setFilteredProductData] = useState(
        products.slice(0, 3)
    );
    const [isLoading, setLoading] = useState(!GupsDataProcessed);
    const tryLink = useLocalizedHref("/try");

    const openDropdown = () => {
        if (isDropdownOpen) {
            return;
        }
        setIsDropdownOpen(true);
    };
    const closeDropdown = () => {
        if (!isDropdownOpen) {
            return;
        }
        setIsDropdownOpen(false);
    };
    const handleClick = () => {
        if (isDropdownOpen) {
            closeDropdown();
        } else {
            openDropdown();
        }
    };

    const getProductData = async () => {
        if (!GupsDataProcessed) {
            try {
                const gupsData = await getGupsData();
                const existingProducts = getExistingProductsList(
                    gupsData as GrowthUnifiedProfile
                );

                const filteredData = getFilteredProductData(
                    products,
                    existingProducts as GUPSProductKeys[]
                );
                setFilteredProductData(filteredData);
            } finally {
                GupsDataProcessed = true;
                setLoading(false);
            }
        }
    };

    useFeatureExposed("gwp-3947-try-now-cta-dropdown", "Test", {
        conditions: [isDropdownOpen]
    });

    return (
        <div
            css={tryNowContainerStyles}
            onMouseLeave={closeDropdown}
            onBlur={closeDropdown}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleClick();
                }
            }}
            onClick={async (e) => {
                e.preventDefault();
                handleClick();
                await getProductData();
                createAnalyticsEvent({
                    eventName: "buttonClicked",
                    eventType: "ui",
                    action: "clicked",
                    actionSubject: "button",
                    actionSubjectId: "globalNavCtaDropdownButtonClicked",
                    source: "globalNav",
                    attributes: {
                        product: "marketing"
                    }
                }).fire();
            }}
        >
            <Button
                css={css({
                    color: wacToken("color.neutral.light0"),
                    transition: "background-color 0.3s ease",
                    minHeight: "40px" // Match the old Adg4Button size
                })}
                variant="primary"
                size="small"
                onMouseEnter={async () => {
                    openDropdown();
                    await getProductData();
                    createAnalyticsEvent({
                        eventName: "buttonHovered",
                        eventType: "ui",
                        action: "hovered",
                        actionSubject: "button",
                        actionSubjectId: "globalNavCtaDropdownButtonHovered",
                        source: "globalNav",
                        attributes: {
                            product: "marketing"
                        }
                    }).fire();
                }}
            >
                {buttonText}
                <div
                    className="caretStyles"
                    css={[caretStyles, isDropdownOpen && rotate180deg]}
                >
                    <Icon name="caret" size="small" />
                </div>
            </Button>
            <div
                css={[
                    isDropdownOpen && openOverlayStyles,
                    !isDropdownOpen && closedOverlayStyles,
                    isDropdownOpen && fadeInAnimation,
                    !isDropdownOpen && fadeOutAnimation
                ]}
            />
            <div
                className="dropdown-menu"
                css={[
                    dropdownStyles,
                    isDropdownOpen && fadeInAnimation,
                    !isDropdownOpen && fadeOutAnimation,
                    isDropdownOpen && displayFlex
                ]}
                onMouseLeave={closeDropdown}
                onBlur={closeDropdown}
                role="menu"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Escape") {
                        closeDropdown();
                    }
                }}
            >
                {isLoading ? (
                    LoadingSpinner
                ) : (
                    <>
                        {FilteredProductData.map((product) => (
                            <a
                                key={product.productName}
                                href={product.signupHref}
                                css={dropdownItemStyles}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    createAnalyticsEvent({
                                        eventType: "ui",
                                        action: "clicked",
                                        actionSubject: "linkButton",
                                        source: "globalNav-cta-desktop-product-link",
                                        attributes: {
                                            productKey: product.productKey,
                                            productName: product.productName,
                                            linkUrl: product.signupHref
                                        }
                                    }).fire();
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.stopPropagation();
                                    }
                                }}
                            >
                                <div
                                    css={productMarkStyles}
                                    className="product-mark"
                                >
                                    {product.productMark}
                                </div>
                                <div css={productTextLayout}>
                                    <p
                                        css={productTitleTextStyles}
                                        className="product-title"
                                    >
                                        {product.productName}
                                    </p>
                                    <p css={productSummaryTextStyles}>
                                        {product.subtitle}
                                    </p>
                                </div>
                            </a>
                        ))}
                        <Link
                            text={intl.formatMessage({
                                id: "globalnav.v2.try-now-dropdown-see-all-products",
                                defaultMessage: "See all products"
                            })}
                            href={tryLink}
                            type={"primary"}
                            css={seeAllProductsStyles}
                            onClick={(e) => {
                                e.stopPropagation();
                                createAnalyticsEvent({
                                    eventType: "ui",
                                    action: "clicked",
                                    actionSubject: "linkButton",
                                    source: "globalNav-cta-desktop-cta-dropdown-content",
                                    attributes: {
                                        label: "see-all-products",
                                        linkUrl: tryLink
                                    }
                                }).fire();
                            }}
                            aria-label={intl.formatMessage({
                                id: "globalnav.v2.try-now-dropdown-see-all-products",
                                defaultMessage: "See all products"
                            })}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

const SearchAndProfile = ({
    // the \"Try now\" CTA in the global nav is meant to be shown by default, to hide it set isShowTryNowLink prop to false
    // read more at https://hello.atlassian.net/wiki/spaces/~857563384/pages/3849594563/Re-introduce+the+global+nav+Try+now+button
    isShowTryNowLink = true,
    productKey = "home",
    isShowSearchButton = true,
    setIsOpenSearch,
    navigationTrynowCtaText,
    navigationTrynowCtaUrl,
    navigationTrynowCtaOnClick,
    searchAndProfileRef,
    isDisplayDivider = true,
    positionTryNowCtaOnRight = false,
    isShowAppSwitcher = true,
    navType = "globalNav",
    navigationTrynowCtaButtonVariant = "primary"
}: // eslint-disable-next-line complexity
SearchAndProfileProps) => {
    const intl = useIntl();
    const [isDisplayCTADropdownButton, setIsDisplayCTADropdownButton] =
        useState(false);
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const { product, edition } = useProductIntent();
    const wacAISearchSummaryExperiment = useExperiment(
        experiments.wacAISearchSummaryV2
    );
    const [isUseGeneralSigninPromptConfig, setIsUseGeneralSigninPromptConfig] =
        useState(false);
    const [renderSearchButton, setRenderSearchButton] = useState(false);
    const [aiEnabled, setAiEnabled] = useState(false);

    // feature gate has configuration to only be enabled on specified page paths
    // https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/gates/wac_sales_inbound_leads
    const isWacSalesInboundLeadsFeatureGateEnabled = checkFeatureFlagGate(
        featureFlags.wacSalesInboundLeads
    );

    const shouldRenderTryNowButton =
        !positionTryNowCtaOnRight && isShowTryNowLink;

    /* TODO: Implement setIsUserLoggedIn, user logged in status (in useEffect), once GWP-51 in implemented  */
    const { isUserLoggedIn, requestInProgress } = useLoggedInUser();
    const defaultCTAUrl = useLocalizedHref("/try");
    const buttonText =
        navigationTrynowCtaText ??
        intl.formatMessage({
            id: "globalnav.v2.try-now-exp-button-label",
            defaultMessage: "Get it free"
        });
    const buttonUrl = navigationTrynowCtaUrl ?? defaultCTAUrl;

    // Experiment Config gwp8058GeneralSigninPromptExperimentCheck
    const gwp8058GeneralSigninPromptExperimentCheck = useExperiment(
        experiments.gwp8058GeneralSigninPromptForExistingUsers
    );

    // Track the last fired enrollment state to prevent re-firing exposure events on layout changes
    const gwp8058LastFiredEnrollmentRef = useRef<string | null>(null);
    const wacAISearchLastFiredEnrollmentRef = useRef<string | null>(null);

    const isNewDay = (lastVisitDate: string): boolean => {
        const lastVisit = new Date(lastVisitDate);
        const today = new Date();
        return (
            lastVisit.getDate() !== today.getDate() ||
            lastVisit.getMonth() !== today.getMonth() ||
            lastVisit.getFullYear() !== today.getFullYear()
        );
    };

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const consecutiveXClicks = Number(
            getLocalStorage("general_signin_prompt_x_clicks", true) || ""
        );
        const lastVisitDate = getLocalStorage(
            "general_signin_prompt_last_visit_timestamp",
            true
        );

        // Only fire exposure if enrollment type has changed, not on every re-render
        if (
            gwp8058GeneralSigninPromptExperimentCheck.type === "enrolled" &&
            gwp8058GeneralSigninPromptExperimentCheck.type !==
                gwp8058LastFiredEnrollmentRef.current
        ) {
            gwp8058GeneralSigninPromptExperimentCheck.fireExposure();
            gwp8058LastFiredEnrollmentRef.current = "enrolled";
        }

        // If user has clicked X 2 times, never show again
        if (consecutiveXClicks >= 2) {
            setIsUseGeneralSigninPromptConfig(false);
        } else if (
            (!lastVisitDate ||
                (lastVisitDate &&
                    consecutiveXClicks > 0 &&
                    isNewDay(lastVisitDate))) &&
            gwp8058GeneralSigninPromptExperimentCheck.type === "enrolled"
        ) {
            setIsUseGeneralSigninPromptConfig(
                gwp8058GeneralSigninPromptExperimentCheck.value
            );
        } else if (
            lastVisitDate &&
            consecutiveXClicks === 0 &&
            gwp8058GeneralSigninPromptExperimentCheck.type === "enrolled"
        ) {
            // Delay showing the signin prompt by 3 seconds
            timeoutId = setTimeout(() => {
                setIsUseGeneralSigninPromptConfig(
                    gwp8058GeneralSigninPromptExperimentCheck.value
                );
            }, 3000);
        } else {
            // catch statement for all other cases
            setIsUseGeneralSigninPromptConfig(false);
        }

        // Cleanup timeout on unmount or effect re-run
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [gwp8058GeneralSigninPromptExperimentCheck]);
    // End of Experiment Config gwp8058GeneralSigninPromptExperimentCheck

    const productLandingPagesData = ComprehensiveProductData();

    useEffect(() => {
        if (navType === "globalNav" && !isUserLoggedIn && !requestInProgress) {
            setIsDisplayCTADropdownButton(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestInProgress, isUserLoggedIn]);

    useEffect(() => {
        if (
            isShowSearchButton &&
            wacAISearchSummaryExperiment &&
            wacAISearchSummaryExperiment.type !== "indeterminate"
        ) {
            setRenderSearchButton(true);
        }

        // Only fire exposure if enrollment type has changed, not on every re-render
        if (
            wacAISearchSummaryExperiment &&
            wacAISearchSummaryExperiment.type === "enrolled" &&
            wacAISearchSummaryExperiment.type !==
                wacAISearchLastFiredEnrollmentRef.current
        ) {
            wacAISearchSummaryExperiment.fireExposure?.();
            wacAISearchLastFiredEnrollmentRef.current = "enrolled";
        }

        if (
            isShowSearchButton &&
            wacAISearchSummaryExperiment &&
            wacAISearchSummaryExperiment.type === "enrolled" &&
            wacAISearchSummaryExperiment.value
        ) {
            setAiEnabled(true);
        }
    }, [isShowSearchButton, wacAISearchSummaryExperiment]);

    return (
        <div
            css={css({
                display: "flex",
                flexDirection: "row",
                gap: "16px"
            })}
            ref={searchAndProfileRef}
        >
            {(isWacSalesInboundLeadsFeatureGateEnabled && (
                <ContactSalesCta />
            )) ||
                (shouldRenderTryNowButton && (
                    <TryIntent
                        intentType={"button"}
                        containerId={"navbar"}
                        productKey={product?.key}
                        edition={edition}
                    >
                        <div css={tryNowContainerStyles}>
                            {isDisplayCTADropdownButton ? (
                                // if the dropdown exp is enabled, show dropdown version of button with the try now/get it free variation,
                                // else show the regular button with the existing experiment conditions
                                <TryNowDropdownMenu
                                    products={productLandingPagesData}
                                    buttonText={buttonText}
                                    productKey={productKey}
                                />
                            ) : (
                                <Button
                                    css={css({
                                        minHeight: "40px" // Match the old Adg4Button size
                                    })}
                                    variant={navigationTrynowCtaButtonVariant}
                                    size="small"
                                    href={buttonUrl}
                                    dataEventProps={{
                                        "data-product-key": productKey,
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-event-container": "navbar",
                                        "data-label": buttonText,
                                        "data-label-english": "Get it free",
                                        "data-track-event": `${productKey}.get-it-free-button-clicked`
                                    }}
                                    onClick={(
                                        e: React.MouseEvent<HTMLButtonElement>
                                    ) => {
                                        if (navigationTrynowCtaOnClick) {
                                            navigationTrynowCtaOnClick(e);
                                        } else if (buttonUrl) {
                                            window.location.href = buttonUrl;
                                        }
                                    }}
                                >
                                    {buttonText}
                                </Button>
                            )}
                        </div>
                    </TryIntent>
                ))}
            <div
                css={css({
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                })}
            >
                {isShowSearchButton && renderSearchButton && (
                    <SearchButton
                        buttonId={"global-nav-search-bar-icon-desktop"}
                        aiEnabled={aiEnabled}
                        onClick={(e) => {
                            e.preventDefault();
                            setIsOpenSearch(true);
                            createAnalyticsEvent({
                                eventName: "searchOpen",
                                eventType: "ui",
                                action: "clicked",
                                actionSubject: "button",
                                actionSubjectId: "searchButtonDesktop",
                                source: "globalNav",
                                attributes: {
                                    product: "search",
                                    pageUrl: window.location.href,
                                    pagePath: window.location.pathname,
                                    aiEnabled: aiEnabled
                                }
                            }).fire();
                        }}
                    />
                )}
            </div>
            {isDisplayDivider ? <div css={dividerStyle} /> : null}
            <div css={navigationActionStyle}>
                <div css={navigationActionLinkStyle}>
                    <Suspense>
                        {isUserLoggedIn && isShowAppSwitcher && (
                            <div css={appSwitcherContainerStyle}>
                                <WacAppSwitcher
                                    // @ts-ignore
                                    allowJoinSitesProvider={true}
                                    appSwitcherLabel={""}
                                    appSwitcherIconPrimaryColor={"#42526e"}
                                    isUseV2AppSwitcherStyle={true}
                                    isDisplayBorderAndHoverEffect={false}
                                />
                            </div>
                        )}
                    </Suspense>
                </div>
                <ExperimentView
                    experiment={experiments.gwp8911NotificationBell}
                    render={(value) => {
                        if (value === "test") {
                            return <NotificationsComponent />;
                        }
                        return <></>;
                    }}
                    notEnrolled={<></>}
                />
                <div css={navigationActionLinkStyle}>
                    <GlobalNavUserProfile
                        isUseNewLoggedInUserDropdown={true}
                        isUseGeneralSigninPromptConfig={
                            isUseGeneralSigninPromptConfig
                        }
                        setIsUseGeneralSigninPromptConfig={
                            setIsUseGeneralSigninPromptConfig
                        }
                        positionTryNowCtaOnRight={positionTryNowCtaOnRight}
                    />
                </div>
                {positionTryNowCtaOnRight && isShowTryNowLink && (
                    <TryIntent intentType={"button"} containerId={"navbar"}>
                        <div
                            css={css({
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                padding: "20px"
                            })}
                        >
                            <Button
                                css={css({
                                    minHeight: "40px" // Match the old Adg4Button size
                                })}
                                variant={navigationTrynowCtaButtonVariant}
                                size="small"
                                href={buttonUrl}
                                dataEventProps={{
                                    "data-product-key": productKey,
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-event-container": "navbar",
                                    "data-label": buttonText,
                                    "data-label-english": "Get it free",
                                    "data-track-event": `${productKey}.get-it-free-button-clicked`
                                }}
                                onClick={(
                                    e: React.MouseEvent<HTMLButtonElement>
                                ) => {
                                    if (navigationTrynowCtaOnClick) {
                                        navigationTrynowCtaOnClick(e);
                                    } else if (buttonUrl) {
                                        window.location.href = buttonUrl;
                                    }
                                }}
                            >
                                {buttonText}
                            </Button>
                        </div>
                    </TryIntent>
                )}
            </div>
        </div>
    );
};

export default SearchAndProfile;
